import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { ProfileSchema, type Profile } from '@etiquetachef/types';
import { firestore } from './firebase';
import { snapshotData } from './firestoreNormalize';

const TAG = 'admin/firestoreProfiles';

/**
 * Assina `/profiles/{uid}` (perfil global do usuário). O telefone verificado
 * por OTP do OWNER vive aqui — não no doc `member` do tenant — então o painel
 * precisa ler o perfil para exibir o contato do dono. Read liberado para
 * super-admin nas `firestore.rules`. Devolve `null` se o doc não existe, está
 * fora do schema ou a leitura falha (erro logado, nunca silenciado).
 *
 * Origem: sem issue — commit 10949c80e, 2026-06-27 (contato do owner no
 * detalhe do tenant).
 *
 * Usado por: `features/members/useProfiles.ts`.
 *
 * Arquitetura: rule `match /profiles/{uid}`. Perfil é global (sem tenant).
 *
 * Dados: `profiles/{uid}` — schema `ProfileSchema`; o telefone verificado do
 * dono mora aqui, não em `members`.
 *
 * Falhas conhecidas:
 *  - "Logado, nunca silenciado" vale só para o console: perfil fora do schema
 *    e erro de leitura não vão para a telemetria, e a tela mostra o mesmo
 *    `null` de perfil inexistente (#926).
 */
export function subscribeProfile(
  uid: string,
  onResult: (profile: Profile | null) => void,
): Unsubscribe {
  const ref = doc(firestore, 'profiles', uid);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onResult(null);
        return;
      }
      const parsed = ProfileSchema.safeParse({ uid: snap.id, ...snapshotData(snap) });
      if (parsed.success) {
        onResult(parsed.data);
      } else {
        console.warn(`${TAG}: profile ${uid} fora do schema`, parsed.error.issues);
        onResult(null);
      }
    },
    (err) => {
      console.error(`${TAG}: snapshot error ${uid}`, err);
      onResult(null);
    },
  );
}
